'use client';

import { useRef, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { T } from './V2Tokens';
import {
    DIVISIONS, SOCIAL_PLATFORMS, INTEGRATIONS, REPORT_METRICS, PORTFOLIO_CASES,
    type Division,
} from './V1Data';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Scroll budget (in viewport heights)
const OUTER_VH   = 800; // total outer div height
const RAILS_VH   = 300; // Phase 1: horizontal rails scroll
const HOLD_VH    = 40;  // Phase 2: rails hold at final state
const SLIDE_VH   = 60;  // Phase 3: portfolio slides up from below
// Phase 4: cards flip until "bottom bottom" (~300vh)

const pct = {
    railsEnd:   `${(RAILS_VH / OUTER_VH * 100).toFixed(1)}% top`,
    slideStart: `${((RAILS_VH + HOLD_VH) / OUTER_VH * 100).toFixed(1)}% top`,
    slideEnd:   `${((RAILS_VH + HOLD_VH + SLIDE_VH) / OUTER_VH * 100).toFixed(1)}% top`,
};

// Portfolio panel accent colors — echo each card's gradient theme
const PANEL_ACCENTS = [T.brand, T.coral, T.mint, T.brand400];

// ─── Track 1: Division cards ──────────────────────────────────────────────────

function DivCard({ d }: { d: Division }) {
    return (
        <div style={{
            height: '100%', background: T.bgCard, border: `1px solid ${T.line}`,
            borderRadius: 24, padding: 28, position: 'relative', overflow: 'hidden',
            boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        }}>
            <div style={{ position: 'absolute', top: -40, left: -40, width: 140, height: 140, borderRadius: '50%', background: `radial-gradient(circle, ${d.color}20, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexShrink: 0 }}>
                <div style={{ width: 42, height: 42, borderRadius: 13, background: d.color + '18', border: `1.5px solid ${d.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 17, height: 17, borderRadius: 5, background: d.color }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: d.color, background: d.color + '15', padding: '4px 10px', borderRadius: 7 }}>{d.num}</span>
            </div>
            <h3 style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, margin: '0 0 5px', color: T.ink, flexShrink: 0 }}>{d.ar}</h3>
            <div style={{ fontSize: 10, color: T.ink50, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12, flexShrink: 0 }}>{d.en}</div>
            <p style={{ fontSize: 14, color: T.ink70, lineHeight: 1.65, margin: '0 0 16px', flex: 1 }}>{d.desc}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flexShrink: 0 }}>
                {d.tools.map((t, i) => (
                    <span key={i} style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 99, background: d.color + '12', color: d.color, border: `1px solid ${d.color}30` }}>{t}</span>
                ))}
            </div>
        </div>
    );
}

// ─── Track 2: Capability cards ────────────────────────────────────────────────

function CapPublishCard({ d }: { d: Division }) {
    return (
        <div style={{ height: '100%', background: T.bg, border: `1px solid ${T.line}`, borderRadius: 24, padding: '20px 22px', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexShrink: 0 }}>
                <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, margin: '0 0 3px', color: T.ink }}>{d.ar}</h3>
                    <div style={{ fontSize: 10, color: T.ink50, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{d.en}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: d.color, background: d.color + '15', padding: '3px 9px', borderRadius: 7, flexShrink: 0 }}>{d.num}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, flex: 1, alignContent: 'stretch' }}>
                {SOCIAL_PLATFORMS.map((p, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 6px', background: '#fff', borderRadius: 14, border: `1px solid ${T.line}` }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', background: p.color === '#FFFC00' ? p.color : p.color + '18', fontSize: 13, fontWeight: 700, color: p.color === '#FFFC00' ? '#333' : p.color }}>{p.icon}</div>
                        <div style={{ fontSize: 9, fontWeight: 600, textAlign: 'center', lineHeight: 1.2, color: T.ink }}>{p.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CapIntegrationsCard({ d }: { d: Division }) {
    return (
        <div style={{ height: '100%', background: T.bg, border: `1px solid ${T.line}`, borderRadius: 24, padding: '20px 22px', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexShrink: 0 }}>
                <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, margin: '0 0 3px', color: T.ink }}>{d.ar}</h3>
                    <div style={{ fontSize: 10, color: T.ink50, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{d.en}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: d.color, background: d.color + '15', padding: '3px 9px', borderRadius: 7, flexShrink: 0 }}>{d.num}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, flex: 1, alignContent: 'start' }}>
                {INTEGRATIONS.map((p, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 4px', background: '#fff', borderRadius: 10, border: `1px solid ${T.line}` }}>
                        <div style={{ width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', background: (p.color === '#FFFC00' || p.color === '#FFE01B') ? p.color : p.color + '18', fontSize: 8, fontWeight: 800, color: (p.color === '#FFFC00' || p.color === '#FFE01B') ? '#333' : p.color }}>{p.abbr}</div>
                        <div style={{ fontSize: 7.5, fontWeight: 600, textAlign: 'center', lineHeight: 1.2, color: T.ink70 }}>{p.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CapReportsCard({ d }: { d: Division }) {
    const bars = [55, 70, 45, 80, 65, 90, 75];
    return (
        <div style={{ height: '100%', background: T.bg, border: `1px solid ${T.line}`, borderRadius: 24, padding: '20px 22px', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexShrink: 0 }}>
                <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, margin: '0 0 3px', color: T.ink }}>{d.ar}</h3>
                    <div style={{ fontSize: 10, color: T.ink50, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{d.en}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: d.color, background: d.color + '15', padding: '3px 9px', borderRadius: 7, flexShrink: 0 }}>{d.num}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 10, flexShrink: 0 }}>
                {REPORT_METRICS.map((m, i) => (
                    <div key={i} style={{ padding: '9px 11px', borderRadius: 11, background: m.color + '12', border: `1px solid ${m.color}25` }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.val}</div>
                        <div style={{ fontSize: 9, color: T.ink70, marginTop: 4, lineHeight: 1.3 }}>{m.label}</div>
                    </div>
                ))}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 38 }}>
                    {bars.map((h, i) => (
                        <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0', background: i === 5 ? d.color : d.color + '28' }} />
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: T.ink50 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((l, i) => <span key={i}>{l}</span>)}
                </div>
            </div>
        </div>
    );
}

// ─── Portfolio overlay: deck card ─────────────────────────────────────────────

interface CaseData { client: string; tag: string; metric: string; label: string; bg: string; }

function DeckCard({ c, zIndex, cardRef }: { c: CaseData; zIndex: number; cardRef: RefObject<HTMLDivElement | null> }) {
    return (
        <div
            ref={cardRef as RefObject<HTMLDivElement>}
            style={{
                position: 'absolute', inset: 0, borderRadius: 28, background: c.bg,
                color: '#fff', overflow: 'hidden', zIndex, padding: '44px 40px',
                boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between',
            }}
            dir="rtl"
        >
            <div style={{ position: 'absolute', bottom: -60, left: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
            <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10, fontWeight: 500, letterSpacing: '0.04em' }}>{c.tag}</div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.2 }}>{c.client}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <div style={{ fontSize: 'clamp(80px, 10vw, 130px)', fontWeight: 900, lineHeight: 0.9, letterSpacing: '-0.04em' }}>{c.metric}</div>
                    <div style={{ fontSize: 14, opacity: 0.75, marginTop: 12 }}>{c.label}</div>
                </div>
                <div style={{ width: 52, height: 52, borderRadius: 26, background: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center', fontSize: 22, flexShrink: 0 }}>↗</div>
            </div>
        </div>
    );
}

// ─── Mobile fallback: simple vertical list ────────────────────────────────────

const PANEL_ACCENTS_MOBILE = [T.brand, T.coral, T.mint, T.brand400];

function V2FeaturesMobile() {
    const core = DIVISIONS.slice(0, 5);

    return (
        <div id="divisions" style={{ background: T.bgWhite, padding: '48px 20px' }}>
            {/* Header */}
            <div style={{ marginBottom: 32 }} dir="rtl">
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 12px', borderRadius: T.rPill,
                    background: T.bgCard, border: `1px solid ${T.line}`,
                    fontSize: 12, fontWeight: 600, color: T.ink70, marginBottom: 12,
                }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                    أقسام المنصة
                </span>
                <h2 style={{ fontSize: 'clamp(24px, 6.5vw, 36px)', fontWeight: 900, lineHeight: 1.1, margin: '0', letterSpacing: '-0.025em', color: T.ink }}>
                    Growth Engine — من التحليل إلى <span style={{ color: T.brand }}>النشر</span>.
                </h2>
            </div>

            {/* Division cards: full-width vertical list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 48 }} dir="rtl">
                {core.map((d, i) => (
                    <div key={i} style={{
                        background: T.bgCard, border: `1px solid ${T.line}`,
                        borderRadius: 20, padding: '22px 20px', position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', top: -30, left: -30, width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${d.color}20, transparent 70%)`, pointerEvents: 'none' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div style={{ width: 38, height: 38, borderRadius: 11, background: d.color + '18', border: `1.5px solid ${d.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ width: 14, height: 14, borderRadius: 4, background: d.color }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: d.color, background: d.color + '15', padding: '3px 9px', borderRadius: 7 }}>{d.num}</span>
                        </div>
                        <h3 style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, margin: '0 0 4px', color: T.ink }}>{d.ar}</h3>
                        <div style={{ fontSize: 10, color: T.ink50, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>{d.en}</div>
                        <p style={{ fontSize: 14, color: T.ink70, lineHeight: 1.65, margin: '0 0 14px' }}>{d.desc}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {d.tools.map((t, j) => (
                                <span key={j} style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 99, background: d.color + '12', color: d.color, border: `1px solid ${d.color}30` }}>{t}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Portfolio section */}
            <div id="portfolio" style={{ marginTop: 16 }} dir="rtl">
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 12px', borderRadius: T.rPill,
                    background: T.bgCard, border: `1px solid ${T.line}`,
                    fontSize: 12, fontWeight: 600, color: T.ink70, marginBottom: 20,
                }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                    أعمالنا
                </span>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {PORTFOLIO_CASES.map((c, i) => (
                        <div key={i} style={{
                            borderRadius: 22, background: c.bg, color: '#fff',
                            padding: '32px 24px', overflow: 'hidden', position: 'relative',
                            boxSizing: 'border-box',
                        }}>
                            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
                            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8, fontWeight: 500, letterSpacing: '0.04em' }}>{c.tag}</div>
                            <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2, marginBottom: 20 }}>{c.client}</div>
                            <div style={{ fontSize: 'clamp(52px, 14vw, 80px)', fontWeight: 900, lineHeight: 0.9, letterSpacing: '-0.04em' }}>{c.metric}</div>
                            <div style={{ fontSize: 14, opacity: 0.75, marginTop: 12 }}>{c.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function V2Features() {
    const isMobile      = useIsMobile();
    const outerRef      = useRef<HTMLDivElement>(null);
    const innerRef      = useRef<HTMLElement>(null);
    const headerRef     = useRef<HTMLDivElement>(null);
    const strip1Ref     = useRef<HTMLDivElement>(null);
    const strip2Ref     = useRef<HTMLDivElement>(null);
    const track1WrapRef = useRef<HTMLDivElement>(null);
    const track2WrapRef = useRef<HTMLDivElement>(null);
    const prog1Ref      = useRef<HTMLDivElement>(null);
    const prog2Ref      = useRef<HTMLDivElement>(null);
    const portfolioRef  = useRef<HTMLDivElement>(null);

    // Portfolio card refs
    const card0Ref = useRef<HTMLDivElement>(null);
    const card1Ref = useRef<HTMLDivElement>(null);
    const card2Ref = useRef<HTMLDivElement>(null);
    const card3Ref = useRef<HTMLDivElement>(null);
    const cardRefs = [card0Ref, card1Ref, card2Ref, card3Ref];

    const core    = DIVISIONS.slice(0, 5);
    const publish = DIVISIONS[5];
    const integr  = DIVISIONS[6];
    const reports = DIVISIONS[7];

    useGSAP(() => {
        if (isMobile) return;
        if (
            !outerRef.current || !innerRef.current || !headerRef.current ||
            !strip1Ref.current || !strip2Ref.current ||
            !track1WrapRef.current || !track2WrapRef.current ||
            !prog1Ref.current || !prog2Ref.current || !portfolioRef.current
        ) return;

        const cards = cardRefs.map(r => r.current).filter(Boolean) as HTMLDivElement[];

        // ── Initial states ────────────────────────────────────────────────────

        gsap.set(prog1Ref.current, { scaleX: 0, transformOrigin: 'right center' });
        gsap.set(prog2Ref.current, { scaleX: 0, transformOrigin: 'left center' });
        gsap.set(Array.from(headerRef.current.children), { y: 20, opacity: 0 });

        const allCards1 = strip1Ref.current.querySelectorAll('.v2-fc1');
        const allCards2 = strip2Ref.current.querySelectorAll('.v2-fc2');
        gsap.set(allCards1, { y: 24, opacity: 0 });
        gsap.set(allCards2, { y: 24, opacity: 0 });

        // Portfolio overlay starts below the viewport (clipped by section's overflow:hidden)
        gsap.set(portfolioRef.current, { yPercent: 100 });

        // Portfolio card deck — all start invisible
        gsap.set(cards, { y: 40, scale: 0.94, opacity: 0 });

        // Portfolio text panels — all start invisible
        gsap.set('.v2-port-panel-0, .v2-port-panel-1, .v2-port-panel-2, .v2-port-panel-3', { opacity: 0, y: 22 });

        // ── Phase 0: Header + initial card entrance (non-scrub) ──────────────

        gsap.to(Array.from(headerRef.current.children), {
            y: 0, opacity: 1, stagger: 0.1, duration: 0.7, ease: 'power3.out',
            scrollTrigger: { trigger: innerRef.current, start: 'top 80%' },
        });
        gsap.to([...Array.from(allCards1).slice(0, 2), ...Array.from(allCards2)], {
            y: 0, opacity: 1, stagger: 0.06, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: innerRef.current, start: 'top 80%' },
        });

        // ── Phase 1: Horizontal rails (0 → RAILS_VH) ─────────────────────────

        const railsTrigger = {
            trigger: outerRef.current,
            start: 'top top',
            end: pct.railsEnd,
            scrub: 1.2,
            invalidateOnRefresh: true,
        };

        // Track 1: scroll LEFT
        gsap.to(strip1Ref.current, {
            x: () => -(strip1Ref.current!.scrollWidth - track1WrapRef.current!.clientWidth),
            ease: 'none',
            scrollTrigger: railsTrigger,
        });

        // Track 2: scroll RIGHT (opposite direction)
        const getT2Start = () => -(strip2Ref.current!.scrollWidth - track2WrapRef.current!.clientWidth);
        gsap.set(strip2Ref.current, { x: getT2Start() });
        gsap.to(strip2Ref.current, {
            x: 0,
            ease: 'none',
            scrollTrigger: {
                ...railsTrigger,
                onRefresh: () => { if (strip2Ref.current) gsap.set(strip2Ref.current, { x: getT2Start() }); },
            },
        });

        // Progress bars
        gsap.to(prog1Ref.current, { scaleX: 1, ease: 'none', scrollTrigger: railsTrigger });
        gsap.to(prog2Ref.current, { scaleX: 1, ease: 'none', scrollTrigger: railsTrigger });

        // Remaining Track 1 cards reveal within rails phase
        const railsScrubTl = gsap.timeline({ scrollTrigger: railsTrigger });
        Array.from(allCards1).slice(2).forEach((card, i) => {
            railsScrubTl.to(card, { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }, i * 1.5);
        });

        // ── Phase 3: Portfolio slides up (slideStart → slideEnd) ──────────────

        gsap.to(portfolioRef.current, {
            yPercent: 0,
            ease: 'power2.inOut',
            scrollTrigger: {
                trigger: outerRef.current,
                start: pct.slideStart,
                end: pct.slideEnd,
                scrub: 1,
                invalidateOnRefresh: true,
            },
        });

        // ── Phase 4: Portfolio card flip (slideEnd → bottom bottom) ───────────

        const cardTl = gsap.timeline({
            scrollTrigger: {
                trigger: outerRef.current,
                start: pct.slideEnd,
                end: 'bottom bottom',
                scrub: 1,
                invalidateOnRefresh: true,
            },
        });

        // Card 0 enters
        cardTl.to(cards[0], { y: 0, scale: 1, opacity: 1, duration: 1, ease: 'power3.out' });
        cardTl.to('.v2-port-panel-0', { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }, '<0.2');

        // Hold — read card 0
        cardTl.to({}, { duration: 0.6 });

        // → card 1
        cardTl.to(cards[0], { y: -120, opacity: 0, scale: 0.95, duration: 1, ease: 'power2.in' });
        cardTl.to(cards[1], { y: 0, scale: 1, opacity: 1, duration: 1, ease: 'power3.out' }, '<0.3');
        cardTl.to('.v2-port-panel-0', { opacity: 0, y: -18, duration: 0.45, ease: 'power2.in' }, '<0.1');
        cardTl.to('.v2-port-panel-1', { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }, '<0.2');

        // Hold — read card 1
        cardTl.to({}, { duration: 0.6 });

        // → card 2
        cardTl.to(cards[1], { y: -120, opacity: 0, scale: 0.95, duration: 1, ease: 'power2.in' });
        cardTl.to(cards[2], { y: 0, scale: 1, opacity: 1, duration: 1, ease: 'power3.out' }, '<0.3');
        cardTl.to('.v2-port-panel-1', { opacity: 0, y: -18, duration: 0.45, ease: 'power2.in' }, '<0.1');
        cardTl.to('.v2-port-panel-2', { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }, '<0.2');

        // Hold — read card 2
        cardTl.to({}, { duration: 0.6 });

        // → card 3
        cardTl.to(cards[2], { y: -120, opacity: 0, scale: 0.95, duration: 1, ease: 'power2.in' });
        cardTl.to(cards[3], { y: 0, scale: 1, opacity: 1, duration: 1, ease: 'power3.out' }, '<0.3');
        cardTl.to('.v2-port-panel-2', { opacity: 0, y: -18, duration: 0.45, ease: 'power2.in' }, '<0.1');
        cardTl.to('.v2-port-panel-3', { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' }, '<0.2');

        // Hold — read card 3
        cardTl.to({}, { duration: 0.6 });

        // Card 3 exits — section ends empty
        cardTl.to(cards[3], { y: -120, opacity: 0, scale: 0.95, duration: 1, ease: 'power2.in' });
        cardTl.to('.v2-port-panel-3', { opacity: 0, y: -18, duration: 0.45, ease: 'power2.in' }, '<0.2');

        // Minimal end hold
        cardTl.to({}, { duration: 0.15 });

    }, { scope: outerRef, dependencies: [isMobile] });

    // On mobile: skip the complex GSAP sticky scroll story — render a simple list
    if (isMobile) return <V2FeaturesMobile />;

    return (
        <div ref={outerRef} style={{ height: `${OUTER_VH}vh`, position: 'relative' }}>
            <section
                ref={innerRef}
                id="divisions"
                style={{
                    position: 'sticky',
                    top: 64,
                    height: 'calc(100vh - 64px)',
                    overflow: 'hidden',
                    background: T.bgWhite,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0 56px',
                    boxSizing: 'border-box',
                }}
            >
                {/* ── Header ── */}
                <div
                    ref={headerRef}
                    style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: '26px 0 12px' }}
                >
                    <div>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '5px 12px', borderRadius: T.rPill,
                            background: T.bgCard, border: `1px solid ${T.line}`,
                            fontSize: 12, fontWeight: 600, color: T.ink70,
                        }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                            أقسام المنصة
                        </span>
                        <h2 style={{ fontSize: 'clamp(24px, 2.6vw, 40px)', fontWeight: 900, lineHeight: 1.1, margin: '10px 0 0', letterSpacing: '-0.025em', color: T.ink }}>
                            Growth Engine — من التحليل إلى <span style={{ color: T.brand }}>النشر</span>.
                        </h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 4 }}>
                        <span style={{ fontSize: 12, color: T.ink50 }}>مرّر ←</span>
                        <div style={{ width: 100, height: 3, borderRadius: 2, background: T.brand100, overflow: 'hidden' }}>
                            <div ref={prog1Ref} style={{ width: '100%', height: '100%', background: T.brand, borderRadius: 2 }} />
                        </div>
                    </div>
                </div>

                {/* ── Track 1: divisions scroll LEFT ── */}
                <div ref={track1WrapRef} style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    <div ref={strip1Ref} dir="ltr" style={{ display: 'flex', gap: 14, height: '100%', willChange: 'transform' }}>
                        {core.map((d, i) => (
                            <div key={i} className="v2-fc1" style={{ flexShrink: 0, width: 312, height: '100%' }} dir="rtl">
                                <DivCard d={d} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Track 2 label + opposite progress ── */}
                <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0 6px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.ink50, letterSpacing: '0.08em' }}>← قدرات إضافية</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 100, height: 3, borderRadius: 2, background: T.brand100, overflow: 'hidden' }}>
                            <div ref={prog2Ref} style={{ width: '100%', height: '100%', background: T.brand300, borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 12, color: T.ink50 }}>→ مرّر</span>
                    </div>
                </div>

                {/* ── Track 2: capabilities scroll RIGHT ── */}
                <div ref={track2WrapRef} style={{ flex: 1, overflow: 'hidden', minHeight: 0, paddingBottom: 28 }}>
                    <div ref={strip2Ref} dir="ltr" style={{ display: 'flex', gap: 14, height: '100%', willChange: 'transform' }}>
                        <div className="v2-fc2" style={{ flexShrink: 0, width: 520, height: '100%' }} dir="rtl">
                            <CapPublishCard d={publish} />
                        </div>
                        <div className="v2-fc2" style={{ flexShrink: 0, width: 580, height: '100%' }} dir="rtl">
                            <CapIntegrationsCard d={integr} />
                        </div>
                        <div className="v2-fc2" style={{ flexShrink: 0, width: 520, height: '100%' }} dir="rtl">
                            <CapReportsCard d={reports} />
                        </div>
                    </div>
                </div>

                {/* ── Portfolio overlay — rises from below to cover the rails ── */}
                <div
                    ref={portfolioRef}
                    id="portfolio"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: T.bgWhite,
                        display: 'flex',
                        alignItems: 'stretch',
                        padding: '80px 56px',
                        boxSizing: 'border-box',
                        gap: 48,
                        zIndex: 20,
                    }}
                >
                    {/* Card deck (60%) */}
                    <div style={{ flex: '0 0 60%', position: 'relative' }}>
                        {PORTFOLIO_CASES.map((c, i) => (
                            <DeckCard key={i} c={c} zIndex={4 - i} cardRef={cardRefs[i]} />
                        ))}
                    </div>

                    {/* Dynamic text panels (40%) */}
                    <div
                        style={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        dir="rtl"
                    >
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '5px 12px', borderRadius: T.rPill,
                            background: T.bgCard, border: `1px solid ${T.line}`,
                            fontSize: 12, fontWeight: 600, color: T.ink70,
                            marginBottom: 28, alignSelf: 'flex-start',
                        }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                            أعمالنا
                        </span>

                        <div style={{ position: 'relative', height: 380, flexShrink: 0 }}>
                            {PORTFOLIO_CASES.map((c, i) => (
                                <div
                                    key={i}
                                    className={`v2-port-panel-${i}`}
                                    style={{
                                        position: 'absolute', inset: 0,
                                        opacity: 0,
                                        display: 'flex', flexDirection: 'column', gap: 18,
                                    }}
                                >
                                    <div style={{ fontSize: 11, fontWeight: 600, color: T.ink50, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                                        {c.tag}
                                    </div>
                                    <h2 style={{ fontSize: 'clamp(28px, 3.2vw, 46px)', fontWeight: 900, lineHeight: 1.1, margin: 0, letterSpacing: '-0.025em', color: T.ink }}>
                                        {c.client}
                                    </h2>
                                    <div>
                                        <div style={{ fontSize: 'clamp(54px, 6.5vw, 86px)', fontWeight: 900, lineHeight: 0.9, letterSpacing: '-0.04em', color: PANEL_ACCENTS[i] }}>
                                            {c.metric}
                                        </div>
                                        <div style={{ fontSize: 15, color: T.ink70, marginTop: 14, lineHeight: 1.55 }}>
                                            {c.label}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
